import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Spin, message } from "antd";
// Redux
import { connect } from "react-redux";
import { getUserRoleId, getClientid, getMenuList } from "redux/selectors";
import styled from "styled-components";
import Dropzone from "molecules/Dropzone";
import COLORS from "common/Colors";
import { get, map, includes } from "lodash";
import Document from "./Document";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { DEVICE } from "common/Device";

const StyledDocumentPageTitle = styled.h2`
  font-weight: 500;
  margin-bottom: 0px;
`;

const StyleDocumentRow = styled(Row)`
  padding-top: 20px;
`;

const StyledDocumentDiv = styled.div`
  margin-top: 20px;
  border: solid 1px ${COLORS.PRIMARY};
  border-radius: 5px;
  padding: 10px;
  height: 100%;
  .nodata {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    p {
      letter-spacing: 2px;
      font-weight: bold;
      color: ${COLORS.GREY};
    }
  }
  ${({ height }) =>
    height &&
    `height: ${height}px; min-height: ${height}px; max-height: ${height}px; overflow: auto;`};
  @media ${DEVICE.tablet} {
    margin-top: 20px;
    height: 100%;
    min-height: 100%;
    max-height: 100%;
  }
`;

const StyledDocumentContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
`;

function UploadFiles({ userRoleId, ClientId, menuList, confidential, item }) {
  const [documentList, setDocumentList] = useState([]);
  const [height, setHeight] = useState(200);
  const [fileName, setFileName] = useState("");
  const [isUploading, toggleUploading] = useState(false);
  const [isLoadingDocuments, toggleLoadingDocuments] = useState(false);

  const Menulist = menuList[userRoleId] || Object.values(menuList)[0];

  useEffect(() => {
    getDocumentList();
  }, []);

  const getDocumentList = async () => {
    toggleLoadingDocuments(true);
    const { list } = await CALL_API(`document-list/${ClientId}`, "post", {
      method: "document-list",
      userId: item?.userid,
    });
    const confidentialList = [];
    const nonConfidentialList = [];
    if (list.length > 0) {
      list?.forEach((item) => {
        if (item?.permissionType === "confidential") {
          confidentialList.push(item);
        } else if (item?.permissionType === "non-confidential") {
          nonConfidentialList.push(item);
        }
      });
    }
    setDocumentList(confidential ? confidentialList : nonConfidentialList);
    toggleLoadingDocuments(false);
  };

  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedExtensions = [
        "jpg",
        "jpeg",
        "png",
        "svg",
        "gif",
        "bmp",
        "webp",
        "pdf",
        "xls",
        "xlsx",
        "csv",
        "doc",
        "docx",
        "txt",
        "ppt",
        "pptx",
        "avi",
        "mov",
        "mp4",
        "wmv",
        "zip",
        "rar",
        "7z",
      ];

      for (const file of acceptedFiles) {
        try {
          if (!file) continue;
          if (file.size > maxSize) {
            message.error("File size should not be more than 10MB");
            continue;
          }

          // Fallback values for mobile camera capture
          let localName = file.name || `photo_${Date.now()}.jpg`;
          let mimeType = file.type || "";
          let localExtension = "";

          if (localName.includes(".")) {
            localExtension = localName.split(".").pop().toLowerCase();
          }

          if (!mimeType && localExtension) {
            // Guess MIME from extension
            const extToMime = {
              jpg: "image/jpeg",
              jpeg: "image/jpeg",
              png: "image/png",
              gif: "image/gif",
              bmp: "image/bmp",
              webp: "image/webp",
            };
            mimeType = extToMime[localExtension] || "image/jpeg";
          }

          // If no extension but MIME exists, infer extension
          if (!localExtension && mimeType) {
            localExtension = mimeType.split("/")[1] || "";
          }

          const mimeSubtype = mimeType.split("/")[1] || "";

          console.log("Picked file:", {
            localName,
            localExtension,
            mimeType,
            mimeSubtype,
            file,
          });

          // Accept if MIME indicates common type or extension allowed
          const isAllowed =
            mimeType.startsWith("image/") ||
            mimeType.startsWith("video/") ||
            mimeType.startsWith("text/") ||
            mimeType === "application/pdf" ||
            allowedExtensions.includes(localExtension) ||
            allowedExtensions.includes(mimeSubtype);

          if (!isAllowed) {
            message.error("Invalid file format");
            continue;
          }

          toggleUploading(true);
          const content = await readFileAsDataURL(file);

          const { code } = await CALL_API(
            `upload-document/${ClientId}`,
            "post",
            {
              name: localName,
              type: mimeType || "application/octet-stream",
              content,
              userId: item?.userid,
              permissionType: confidential
                ? "confidential"
                : "non-confidential",
            }
          );

          if (code === STATUS_CODE.SUCCESS) {
            message.success("Document uploaded");
            await getDocumentList();
          } else {
            message.error("Something went wrong!! Please try again.");
          }
        } catch (err) {
          console.error("Upload error:", err);
          message.error("Upload failed. Please try again.");
        } finally {
          toggleUploading(false);
        }
      }
    },
    [ClientId, confidential, item?.userid, getDocumentList]
  );

  const deleteDocument = async (id) => {
    const response = await CALL_API(
      `upload-document/${ClientId}/${id}`,
      "delete",
      {
        // documentId : id, //Commented Code so that it's used in future
        status_id: 0,
      }
    );
    message.success("Document has been Deleted");
    getDocumentList();
  };

  // Ensure uploadDocumentFromApp is always available for the mobile bridge
  if (!window.uploadDocumentFromApp) {
    window.uploadDocumentFromApp = async (base64, name, MIMEtype) => {
      message.success("uploadDocumentFromApp called", {
        base64Length: base64?.length,
        name,
        MIMEtype,
      });
      try {
        const base64Size = Math.ceil((base64.length * 3) / 4); // approximate bytes
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (base64Size > maxSize) {
          message.error("File size should not be more than 10MB");
          return;
        }
        toggleUploading(true);
        const { code } = await CALL_API(`upload-document/${ClientId}`, "post", {
          name,
          type: MIMEtype || "application/octet-stream",
          content: base64,
          userId: item?.userid,
          permissionType: confidential ? "confidential" : "non-confidential",
        });
        console.log("code111--->", code);
        if (code === STATUS_CODE.SUCCESS) {
          message.success("Document uploaded");
          await getDocumentList();
        } else {
          message.error("Something went wrong!! Please try again.");
        }
      } catch (err) {
        console.error("uploadDocumentFromApp error:", err);
        message.error("Upload failed.");
      } finally {
        toggleUploading(false);
      }
    };
    // For Flutter devs: call window.uploadDocumentFromApp(base64, name, mimeType) after taking a photo
  }

  const uploadDocument = () => {
    // detect multiple possible bridges used by mobile apps
    if (window.Print && typeof window.Print.postMessage === "function") {
      window.Print.postMessage("upload-document");
      return;
    }
    if (
      window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === "function"
    ) {
      window.ReactNativeWebView.postMessage("upload-document");
      return;
    }
  };

  return (
    <>
      <StyleDocumentRow gutter={16}>
        {Menulist?.find(
          (item) =>
            item?.key === "HRResources" &&
            item?.subMenu?.find(
              (subItem) =>
                subItem?.key === "documents" && subItem?.permission.create
            )
        ) && (
          <Col md={24} sm={24} xs={24} onClick={uploadDocument}>
            <Spin spinning={isUploading}>
              <Dropzone
                onDrop={onDrop}
                accept="image/*,.png,.jpeg,.jpg,.svg,.xls,.xlsx,.pdf,.csv,.doc,.docx,.txt,.ppt,.avi,.zip,.mov"
                fileName={fileName}
                access={Menulist?.find(
                  (item) =>
                    item?.key === "HRResources" &&
                    item?.subMenu?.find(
                      (subItem) =>
                        subItem?.key === "documents" &&
                        subItem?.permission.create
                    )
                )}
              />
            </Spin>
          </Col>
        )}

        <Col md={24} sm={24} xs={24}>
          <Spin spinning={isLoadingDocuments}>
            <StyledDocumentDiv height={height}>
              {documentList?.length > 0 ? (
                <Row gutter={24}>
                  {map(
                    documentList,
                    ({ documentId, documentName, createdon }, index) => (
                      <Col md={12} lg={8} sm={12} xs={12} key={index}>
                        <StyledDocumentContainer>
                          <Document
                            id={documentId}
                            name={documentName}
                            staffName=""
                            uploadTime={createdon}
                            deleteDocument={deleteDocument}
                            getDocumentList={getDocumentList}
                          />
                        </StyledDocumentContainer>
                      </Col>
                    )
                  )}
                </Row>
              ) : (
                <div className="nodata">
                  <p>No Data</p>
                </div>
              )}
            </StyledDocumentDiv>
          </Spin>
        </Col>
      </StyleDocumentRow>
    </>
  );
}

const mapStateToProps = (state) => ({
  userRoleId: getUserRoleId(state),
  ClientId: getClientid(state),
  menuList: getMenuList(state),
});

export default connect(mapStateToProps, null)(UploadFiles);
